import React, { useState, useMemo } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  Heart, Share2, MessageSquare, Eye, Loader2, ExternalLink, Languages,
  ChevronDown, ChevronUp, BarChart3, Users2, ChevronRight,
} from 'lucide-react';
import { apiClient } from '@/lib/api';
import { PC, PL, SC, fmtNum, isVideoUrl, proxyUrl, relevanceTier, TRANSLATE_LANGUAGES, SocialPost, LinkedAccount } from './types';
import { AuthedImage, useAuthedBlobUrl } from './AuthedImage';

// ─── Relevance badge ──────────────────────────────────────────────────────────

const RelevanceBadge = ({ score, matchedTerms }: { score?: number | null; matchedTerms?: string[] | null }) => {
  const [expanded, setExpanded] = useState(false);
  const tier = relevanceTier(score);
  if (!tier) return null;
  const hasTerms = (matchedTerms?.length ?? 0) > 0;
  return (
    <div className="relative">
      <button
        onClick={() => hasTerms && setExpanded(e => !e)}
        className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-mono"
        style={{ background: `${tier.color}15`, color: tier.color, border: `1px solid ${tier.color}30`, cursor: hasTerms ? 'pointer' : 'default' }}
        title={`Relevance score: ${(score ?? 0).toFixed(2)}`}
      >
        {tier.label} match
        {hasTerms && (expanded ? <ChevronUp size={9} /> : <ChevronDown size={9} />)}
      </button>
      {expanded && hasTerms && (
        <div className="absolute z-20 top-full left-0 mt-1 p-2 rounded-lg min-w-[180px]"
          style={{ background: '#111', border: '1px solid #2a2a2a' }}>
          <p className="text-[9px] text-gray-600 uppercase tracking-widest mb-1.5">Matched on</p>
          <div className="flex flex-wrap gap-1">
            {matchedTerms!.map(t => (
              <span key={t} className="text-[10px] px-1.5 py-0.5 rounded font-mono" style={{ background: `${tier.color}15`, color: tier.color }}>{t}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Linked accounts row (collapsed by default — only fetches on expand) ─────

const LinkedAccountsRow = ({ platform, authorId }: { platform: string; authorId: string }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [linked, setLinked] = useState<LinkedAccount[] | null>(null);

  const handleOpen = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (linked !== null || !authorId) return;
    setLoading(true);
    try {
      const res: any = await apiClient.getLinkedAccounts(platform, authorId);
      setLinked(res?.linked_accounts ?? []);
    } catch { setLinked([]); }
    finally { setLoading(false); }
  };

  if (!authorId) return null;

  return (
    <div className="px-4 pb-2">
      <button onClick={handleOpen} className="flex items-center gap-1 text-[10px] text-gray-600 hover:text-gray-400">
        <Users2 size={10} />
        Linked accounts
        {open ? <ChevronUp size={9} /> : <ChevronRight size={9} />}
      </button>
      {open && (
        <div className="mt-1.5 pl-1">
          {loading && <span className="text-[10px] text-gray-700 flex items-center gap-1"><Loader2 size={9} className="animate-spin" /> Checking...</span>}
          {!loading && linked?.length === 0 && <span className="text-[10px] text-gray-700 italic">No linked accounts found yet.</span>}
          {!loading && (linked?.length ?? 0) > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {linked!.map(acc => (
                <span key={`${acc.platform}-${acc.author_id}`} className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-mono"
                  style={{ background: `${PC[acc.platform] ?? PC.default}15`, color: PC[acc.platform] ?? PC.default, border: `1px solid ${PC[acc.platform] ?? PC.default}30` }}
                  title={`Matched via: ${acc.matched_via.join(', ')}`}>
                  {PL[acc.platform] ?? acc.platform}: {acc.handle || acc.author_id}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Authenticated video (same auth requirement as AuthedImage, but videos
// here always went through the proxy unconditionally, never direct) ─────────

const AuthedVideo = ({ proxySrc, className, style }: { proxySrc: string; className?: string; style?: React.CSSProperties }) => {
  const blobUrl = useAuthedBlobUrl(proxySrc);
  if (!blobUrl) return <div className={className} style={{ ...style, background: '#000' }} />;
  return <video src={blobUrl} controls className={className} style={style} />;
};

// ─── PostCard ─────────────────────────────────────────────────────────────────

interface PostCardProps {
  post: SocialPost;
  keyword: string;
  onAnalyze: (p: SocialPost) => void;
  showLinkedAccounts?: boolean;
}

export const PostCard: React.FC<PostCardProps> = ({ post, keyword, onAnalyze, showLinkedAccounts = true }) => {
  const [expanded, setExpanded] = useState(false);
  const [translatedText, setTranslatedText] = useState<string | null>(null);
  const [translating, setTranslating] = useState(false);
  const [showLangPicker, setShowLangPicker] = useState(false);

  const color = PC[post.platform] ?? PC.default;
  const sentColor = SC[post.sentiment] ?? SC.unknown;

  const handleTranslate = async (targetLang: string) => {
    setShowLangPicker(false);
    setTranslating(true);
    try {
      const res = await apiClient.translatePost(post.text, targetLang);
      setTranslatedText(res?.translated_text ?? '');
    } catch { setTranslatedText('[Translation failed]'); }
    finally { setTranslating(false); }
  };

  const sourceText = translatedText ?? post.text;

  // Highlight every matched term when relevance scoring found some; fall back
  // to the literal search keyword otherwise (e.g. a stream's Detections view
  // where relevance wasn't recomputed against a live query).
  const highlightTerms = useMemo(() => {
    const terms = (post.matched_terms?.length ? post.matched_terms : (keyword ? [keyword] : [])).filter(Boolean);
    return [...new Set(terms.map(t => t.toLowerCase()))];
  }, [post.matched_terms, keyword]);

  const displayText = useMemo<React.ReactNode>(() => {
    if (highlightTerms.length === 0) return sourceText;
    const escaped = highlightTerms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).sort((a, b) => b.length - a.length);
    const parts = sourceText.split(new RegExp(`(${escaped.join('|')})`, 'gi'));
    return parts.map((p, i) =>
      highlightTerms.includes(p.toLowerCase())
        ? <mark key={i} style={{ background: '#00ff0025', color: '#00ff00', borderRadius: 2, padding: '0 1px' }}>{p}</mark>
        : <React.Fragment key={i}>{p}</React.Fragment>
    );
  }, [sourceText, highlightTerms]);

  const totalEng = post.likes + post.shares + post.comments + post.views;

  return (
    <div
      className="mb-3 rounded-lg overflow-hidden transition-all hover:brightness-110"
      style={{ border: '1px solid rgba(255,255,255,0.07)', borderLeft: `3px solid ${color}`, background: 'rgba(10,10,10,0.97)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0"
            style={{ background: `${color}20`, color }}>
            {(PL[post.platform] ?? post.platform)[0].toUpperCase()}
          </div>
          <span className="text-sm font-semibold text-white">
            {post.author}
            {post.author_verified && <span className="ml-1 text-xs" style={{ color: '#60a5fa' }}>✓</span>}
          </span>
          {post.author_followers > 0 && (
            <span className="text-[10px] text-gray-500 font-mono">{fmtNum(post.author_followers)}</span>
          )}
          <span className="text-[10px] px-1.5 py-0.5 rounded font-mono"
            style={{ background: `${color}15`, color, border: `1px solid ${color}30` }}>
            {PL[post.platform] ?? post.platform}
          </span>
          <RelevanceBadge score={post.relevance_score} matchedTerms={post.matched_terms} />
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="w-1.5 h-1.5 rounded-full" style={{ background: sentColor, boxShadow: `0 0 5px ${sentColor}` }} />
          <span className="text-[10px] text-gray-500">
            {formatDistanceToNow(new Date(post.timestamp), { addSuffix: true })}
          </span>
        </div>
      </div>

      {/* Text */}
      <div className="px-4 pb-2">
        <p className="text-sm text-gray-200 leading-relaxed whitespace-pre-wrap break-words">
          {displayText}
        </p>
        {sourceText.length > 320 && (
          <button onClick={() => setExpanded(e => !e)}
            className="text-[11px] text-gray-600 hover:text-gray-400 mt-1 flex items-center gap-1">
            {expanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
            {expanded ? 'Collapse' : `Show full (${sourceText.length} chars)`}
          </button>
        )}
        {translatedText && (
          <p className="text-[10px] text-gray-600 mt-1 italic">↳ Translated from {post.language?.toUpperCase()}</p>
        )}
      </div>

      {/* Media — single item gets a tall object-contain box so nothing is
          cropped (a fixed-height object-cover box was clipping portrait
          video/images and, worse, clipping video controls); a real multi-item
          grid (2-4) keeps object-cover tiles, matching how Twitter/Instagram
          themselves crop multi-image grids — that crop is expected there,
          it was only ever a bug for the single-item case. */}
      {post.media_urls.length > 0 && (
        <div className="px-4 pb-3">
          <div className={`grid gap-1 rounded overflow-hidden ${post.media_urls.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
            {post.media_urls.slice(0, 4).map((url, i) => {
              const single = post.media_urls.length === 1;
              return isVideoUrl(url)
                ? (
                  <AuthedVideo key={i} proxySrc={proxyUrl(url)}
                    className="w-full object-contain rounded bg-black"
                    style={{ maxHeight: single ? 420 : 200 }} />
                )
                : (
                  <div key={i} className="relative bg-black rounded overflow-hidden flex items-center justify-center"
                    style={{ minHeight: single ? 160 : 120, maxHeight: single ? 420 : 200 }}>
                    <AuthedImage src={url} proxySrc={proxyUrl(url)}
                      className={single ? 'w-full h-full object-contain' : 'w-full h-full object-cover'}
                      style={{ maxHeight: single ? 420 : 200 }} />
                  </div>
                );
            })}
          </div>
          {post.media_urls.length > 4 && (
            <p className="text-[9px] text-gray-600 mt-1 font-mono">+{post.media_urls.length - 4} more media</p>
          )}
        </div>
      )}

      {/* Hashtags */}
      {post.hashtags.length > 0 && (
        <div className="px-4 pb-2 flex flex-wrap gap-1">
          {post.hashtags.slice(0, 10).map(tag => (
            <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded font-mono"
              style={{ background: '#111', color: `${color}bb`, border: `1px solid ${color}20` }}>
              #{tag}
            </span>
          ))}
        </div>
      )}

      {showLinkedAccounts && <LinkedAccountsRow platform={post.platform} authorId={post.author_id} />}

      {/* Footer */}
      <div className="px-4 py-2 flex items-center justify-between" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div className="flex items-center gap-3 text-[11px] text-gray-600">
          {post.likes > 0 && <span className="flex items-center gap-1 text-pink-600"><Heart size={10} /> {fmtNum(post.likes)}</span>}
          {post.shares > 0 && <span className="flex items-center gap-1 text-blue-600"><Share2 size={10} /> {fmtNum(post.shares)}</span>}
          {post.comments > 0 && <span className="flex items-center gap-1 text-green-600"><MessageSquare size={10} /> {fmtNum(post.comments)}</span>}
          {post.views > 0 && <span className="flex items-center gap-1 text-gray-500"><Eye size={10} /> {fmtNum(post.views)}</span>}
          {totalEng === 0 && <span className="text-gray-700 italic">No engagement data</span>}
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <button onClick={() => setShowLangPicker(v => !v)} disabled={translating}
              className="flex items-center gap-1 text-[10px] text-gray-600 hover:text-gray-300 disabled:opacity-40">
              {translating ? <Loader2 size={10} className="animate-spin" /> : <Languages size={10} />}
              {translatedText ? 'Re-translate' : 'Translate'}
            </button>
            {showLangPicker && (
              <div className="absolute z-20 bottom-full right-0 mb-1 p-1.5 rounded-lg grid grid-cols-2 gap-1 w-[220px]"
                style={{ background: '#111', border: '1px solid #2a2a2a' }}>
                {TRANSLATE_LANGUAGES.map(l => (
                  <button key={l.value} onClick={() => handleTranslate(l.value)}
                    className="text-[10px] px-2 py-1 rounded text-left text-gray-400 hover:bg-white/5 hover:text-white">
                    {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {post.url && (
            <a href={post.url} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1 text-[10px] text-gray-600 hover:text-gray-300">
              <ExternalLink size={10} /> Source
            </a>
          )}
          <button
            onClick={() => onAnalyze(post)}
            className="flex items-center gap-1 text-[10px] hover:text-white transition-colors px-2 py-0.5 rounded"
            style={{ color: '#00ff0080', border: '1px solid #00ff0020', background: '#00ff0008' }}>
            <BarChart3 size={10} /> Analyse
          </button>
        </div>
      </div>
    </div>
  );
};

export default PostCard;
